import { IdentityVerificationStatus } from '@/services/ops/kyc/IdentityVerificationService';
import ParticipantService from '@/services/Participant.service';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import {
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import _ from 'lodash';
import { FC, useCallback, useMemo } from 'react';

const IDV_UNKNOWN = 'unknown';
const IDV_ACTIVE = 'active';
const IDV_PENDING_REVIEW = 'pending_review';
const IDV_SUCCESS = 'success';

type DistributionIdVerStatusProps = {
  distributionId: number;
  distributionType: 'LOAN' | 'WITHDRAWAL';
};

export const DistributionIdVerStatus: FC<
  DistributionIdVerStatusProps
> = props => {
  const verificationStatus = useQuery<IdentityVerificationStatus>(
    [
      'ParticipantService.getIdentityVerification',
      props.distributionType,
      props.distributionId
    ],
    async () => {
      return props.distributionType == 'WITHDRAWAL'
        ? await ParticipantService.getWithdrawalIdentityVerification(
            props.distributionId
          )
        : {};
    },
    {
      enabled:
        Boolean(props.distributionId) && props.distributionType == 'WITHDRAWAL'
    }
  );

  const reloadVerificationStatus = useCallback(
    async () => await verificationStatus.refetch(),
    []
  );

  const idvStatus = useMemo(
    () => verificationStatus.data?.idVerResponse?.status ?? IDV_UNKNOWN,
    [verificationStatus]
  );

  const idvStatusText = useMemo(() => {
    return _.startCase(idvStatus);
  }, [idvStatus]);

  const plaidDashboardHref =
    verificationStatus.data?.idVerResponse?.dashboardLink;

  return (
    <>
      {!verificationStatus.isFetching && (
        <Stack alignItems='center' direction='row' spacing={0.5}>
          <Tooltip title='Click to reload'>
            {idvStatus === IDV_UNKNOWN ? (
              <PendingIcon
                color='disabled'
                fontSize='small'
                onClick={reloadVerificationStatus}
              />
            ) : [IDV_PENDING_REVIEW, IDV_ACTIVE].includes(idvStatus) ? (
              <PendingIcon
                color='warning'
                fontSize='small'
                onClick={reloadVerificationStatus}
              />
            ) : idvStatus === IDV_SUCCESS ? (
              <CheckCircleIcon
                color='success'
                fontSize='small'
                onClick={reloadVerificationStatus}
              />
            ) : (
              <ErrorIcon
                color='error'
                fontSize='small'
                onClick={reloadVerificationStatus}
              />
            )}
          </Tooltip>
          <Typography variant='body2'>
            {plaidDashboardHref ? (
              <Link href={plaidDashboardHref} target='_blank'>
                {idvStatusText}
              </Link>
            ) : (
              <>{idvStatusText}</>
            )}
          </Typography>
        </Stack>
      )}
      {verificationStatus.isFetching && <CircularProgress size={15} />}
    </>
  );
};
